import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type CollapsibleRegionProps = {
  /** Whether the region is expanded. Both directions animate. */
  open: boolean;
  /** Accessible id referenced by the controlling trigger's `aria-controls`. */
  id?: string;
  className?: string;
  children: ReactNode;
};

/**
 * Smooth open/close wrapper for accordion-style panels. Animates height via
 * `grid-template-rows` (`0fr` <-> `1fr`) plus opacity, so collapse transitions
 * instead of instantly unmounting (CSS cannot animate to/from `height: auto`,
 * and conditional rendering cannot animate unmount at all).
 *
 * Content stays mounted while closed: `inert` removes it from tab order and
 * assistive tech immediately, while `invisible` flips after the close
 * transition via the visibility transition. Respects reduced-motion by
 * disabling the transition entirely.
 */
export function CollapsibleRegion({ open, id, className, children }: CollapsibleRegionProps) {
  return (
    <div
      id={id}
      inert={!open}
      className={cn(
        "grid transition-[grid-template-rows,opacity,visibility] duration-300 ease-[cubic-bezier(0.87,0,0.13,1)] motion-reduce:transition-none",
        open ? "grid-rows-[1fr] opacity-100" : "invisible grid-rows-[0fr] opacity-0",
        className,
      )}>
      <div className='min-h-0 overflow-hidden'>{children}</div>
    </div>
  );
}
